import React, { useState, useEffect } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { Form, Input, Label, Button, Row, Col } from 'reactstrap'
import { get, isEmpty } from 'lodash'
import moment from 'moment'
import CustomDatePicker from '../CustomDatePicker'
import PopupError from '../PopupError'
import '../css/EditCreate.css'
import { formatEditDate, formatInTimeZoneDate, formatInTimeZoneDateTime } from '../../helpers'
import useFormHooks from '../../hooks/useFormHooks'
import { generateTableColumns, renderOptions } from '../../helpers/helperFunctions'
import { downloadFile } from '../../helpers/download';

import {
    createRedemptionCodes,
    getMaxRedemptionCodesQuantity,
    getRedemptionBranches,
    getRedemptionCodesById,
    getRedemptionGroups,
    getRedemptionProducts,
    updateRedemptionGroup,
    getBranchById,
    getRedemptionCodesByIdReport
} from '../../api/redemptionCodesApi'
import { alphanumeric } from '../../helpers/regex'
import usePrevious from '../../hooks/usePrevious'
import RequiredFieldNotation from '../utils/RequiredFieldNotation'
import InputFeedback from '../utils/InputFeedback';
import feedback from '../utils/feedback';
import { customLoadingOverlay } from '../css/CustomLoadingOverlay'
import LoadingOverlay from 'react-loading-overlay-ts'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DataTableBase from '../utils/DataTable';

const initialInputState = {
    name: '',
    qty: 1,
    branchId: '',
    productId: '',
    startDate: '',
    endDate: '',
    redemptionGroupId: '',
    createdDate: '',
    createdBy: '',
    updatedBy: '',
    updatedDate: '',
}

const initialRedemptionCodes = {
    currentPage: 0,
    pageCount: 1,
    pageSize: 10,
    results: [],
    rowCount: 0,
    orderBy: 'code',
    descending: true,
}

const initialBranch = {
    branchId: 0,
    branchCode: 0,
    name: '',
    startDate: null,
    endDate: null,
    clubId: 0,
    clubName: '',
    customerServiceEmail: null,
    systemTimeZoneId: null,
    systemTimeZoneName: null,
    branchIanaTimeZoneId: null,
    createdBy: '',
    updatedBy: '',
    updatedDate: '',
    createdDate: ''
}

const RedemptionCodesCreate = () => {
    const location = useLocation()
    const history = useHistory()

    const [loading, setLoading] = useState(false)
    const [branches, setBranches] = useState([])
    const [products, setProducts] = useState([])
    const [inputError, setInputError] = useState({})
    const [editing, setEditing] = useState(false)

    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    const [branch, setBranch] = useState(initialBranch)
    const [redemptionCodes, setRedemptionCodes] = useState(initialRedemptionCodes)
    const [isLoading, setIsLoading] = useState(true)

    const [apiErrors, setApiErrors] = useState([])

    const [haveDataFromLocation, setHaveDataFromLocation] = useState(!!get(location, 'state'))

    const [maxRedemptionCodeQuantity, setMaxRedemptionCodeQuantity] = useState(10000)

    useEffect(() => {
        const fetchMaxRedemptionCodesQuantity = async () => {
            getMaxRedemptionCodesQuantity({ callback: setMaxRedemptionCodeQuantity })
        }
        fetchMaxRedemptionCodesQuantity()
    }, [])

    const handleErrorClose = () => {
        setApiErrors([])
    }

    const handleRedemptionCodesChange = redemptionCodesData => {
        setRedemptionCodes({ ...redemptionCodes, ...redemptionCodesData })
    }

    const handleBranchChange = branchData => {
        setBranch(branchData)
        setStartDate(formatInTimeZoneDate(get(inputs, 'startDate'), branchData.branchIanaTimeZoneId))
        setEndDate(formatInTimeZoneDate(get(inputs, 'endDate'), branchData.branchIanaTimeZoneId))
    }

    const goBack = () => history.goBack()

    const inputsState = haveDataFromLocation
        ? {
            ...initialInputState,
            ...get(location, 'state'),
            name: get(location, 'state.redemptionName'),
        }
        : initialInputState

    const inputRedemptionValidator = (inputs = {}) => {
        let errorObject = {}
        Object.entries(inputs).forEach(input => {
            switch (input[0]) {
                case 'name':
                    if (!input[1]) errorObject = { ...errorObject, [input[0]]: 'Value is required.' }
                    if (!input[1].match(alphanumeric))
                        errorObject = {
                            ...errorObject,
                            [input[0]]: 'Value must contain only letters and/or numbers.',
                        }
                    break
                case 'qty':
                    if (input[1] < 1)
                        errorObject = { ...errorObject, [input[0]]: 'Quantity must be greater than 0.' }
                    if (parseInt(input[1]) > parseInt(maxRedemptionCodeQuantity))
                        errorObject = {
                            ...errorObject,
                            [input[0]]: `Quantity must be less than ${maxRedemptionCodeQuantity}.`,
                        }
                    break
                case 'branchId':
                    if (!input[1]) errorObject = { ...errorObject, [input[0]]: 'Value is required.' }
                    break
                case 'productId':
                    if (!input[1] && !(input[1] > -1))
                        errorObject = { ...errorObject, [input[0]]: 'Value is required.' }
                    break
                case 'startDate':
                    if (!input[1]) errorObject = { ...errorObject, [input[0]]: 'Value is required.' }
                    if (moment(input[1]).isSameOrAfter(get(inputs, 'endDate'), 'day'))
                        errorObject = { ...errorObject, [input[0]]: 'Start date must be before end date.' }
                    break
                case 'endDate':
                    if (!input[1]) errorObject = { ...errorObject, [input[0]]: 'Value is required.' }
                    break
                default:
                    break
            }
        })
        setInputError(errorObject)
        return !isEmpty(errorObject)
    }

    const isEditing = haveDataFromLocation && !editing

    const handleRedemptionSubmit = (inputs = {}, setInputs = () => { }) => {
        setLoading(true)

        if (isEditing) {
            setEditing(true)
            setLoading(false)
            return null
        }

        setInputError({})
        const haveErrors = inputRedemptionValidator(inputs)

        if (haveErrors) {
            setLoading(false)
            return null
        }

        const redemptionData = {
            name: get(inputs, 'name'),
            qty: get(inputs, 'qty'),
            branchId: get(inputs, 'branchId'),
            productId: get(inputs, 'productId'),
            startDate: get(inputs, 'startDate').substring(0, 10),
            endDate: get(inputs, 'endDate').substring(0, 10),
            ...(get(inputs, 'redemptionGroupId') && {
                redemptionGroupId: get(inputs, 'redemptionGroupId'),
            }),
        }

        const errorRedemptionCallback = errors => {
            setApiErrors(errors)
            setLoading(false)
        }

        if (haveDataFromLocation) {
            const handleUpdateRedemptionCodeCallback = resData => {
                setInputs({ ...resData })
                setEditing(false)
                setLoading(false)
            }

            updateRedemptionGroup({
                callback: handleUpdateRedemptionCodeCallback,
                redemptionData,
                errorCallback: errorRedemptionCallback,
            })

            return null
        }

        const handleCreateRedemptionCodeCallback = resData => {
            setHaveDataFromLocation(true)
            setInputs({ ...resData })
        }

        createRedemptionCodes({
            callback: data => {
                handleCreateRedemptionCodeCallback(data)
                setLoading(false)
            },
            redemptionData,
            errorCallback: errorRedemptionCallback,
        })

        return null
    }

    const { handleSubmit, handleInputChange, inputs, setInputs } = useFormHooks(inputsState, () =>
        handleRedemptionSubmit(inputs, setInputs)
    )

    const handlePageSizeChange = async pageSize => {
        setRedemptionCodes({
            ...redemptionCodes,
            pageSize,
            filter: get(inputs, 'redemptionGroupId'),
        })
    }

    const handlePageChange = async currentPage => {
        try {
            setIsLoading(true)
            await getRedemptionCodesById({
                callback: handleRedemptionCodesChange,
                id: get(inputs, 'redemptionGroupId'),
                pageSize: get(redemptionCodes, 'pageSize'),
                currentPage,
                orderBy: get(redemptionCodes, 'orderBy'),
                descending: get(redemptionCodes, 'descending'),
            })
            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        const handleBranch = async () => {
            try {
                await setIsLoading(true)
                await getBranchById({
                    callback: handleBranchChange,
                    id: get(inputs, 'branchId'),
                })
                await setIsLoading(false)
            } catch (error) {
                await setIsLoading(false)
            }
        }

        if (haveDataFromLocation) {
            handleBranch()
        }
    }, [
        inputs.branchId,
        haveDataFromLocation,
        editing,
    ])

    useEffect(() => {
        const handleRedemptionCodes = async () => {
            try {
                await setIsLoading(true)
                await getRedemptionCodesById({
                    callback: handleRedemptionCodesChange,
                    id: get(inputs, 'redemptionGroupId'),
                    pageSize: get(redemptionCodes, 'pageSize'),
                    orderBy: get(redemptionCodes, 'orderBy'),
                    descending: get(redemptionCodes, 'descending'),
                })
                await setIsLoading(false)
            } catch (error) {
                await setIsLoading(false)
            }
        }

        if (haveDataFromLocation) handleRedemptionCodes()
    }, [
        inputs.redemptionGroupId,
        redemptionCodes.pageSize,
        redemptionCodes.orderBy,
        redemptionCodes.descending,
        haveDataFromLocation,
        editing,
    ])

    useEffect(() => {
        if (!haveDataFromLocation) {
            getRedemptionBranches({ callback: setBranches })
        }
    }, [haveDataFromLocation])

    useEffect(() => {
        if (!haveDataFromLocation && get(inputs, 'branchId'))
            getRedemptionProducts({ callback: setProducts, branchId: get(inputs, 'branchId') })
    }, [inputs.branchId, haveDataFromLocation])

    const handleDatePickerChange = (date, name) => {
        const onChangeEvent = {
            persist: () => { },
            target: {
                name,
                value: date,
            },
        }

        handleInputChange(onChangeEvent)
    }

    const columns = [
        {
            name:
                <div
                    style={{
                        textIndent: 10,
                        textAlign: "left",
                        width: 420,
                    }}
                >
                    Code <FontAwesomeIcon icon="sort" size="sm" />
                </div>,
            width: '420px',
            selector: row => row.code,
            sortable: true,
            sortField: 'code',
            cell: row => <div><p style={{ marginLeft: "10px", }} className="cursor">{row.code}</p></div>
        },
        {
            name:
                <div
                    style={{
                        textIndent: 10,
                        textAlign: "left",
                    }}
                >
                    Status <FontAwesomeIcon icon="sort" size="sm" />
                </div>,
            selector: row => row.status,
            sortable: true,
            sortField: 'code',
            cell: row => <div><p style={{ marginLeft: "10px", }} className="cursor">{row.status}</p></div>
        }
    ]

    const handleOrderData = event => {
        const orderByEvent = event.sortField
        const descEvent = get(redemptionCodes, 'descending')
        setRedemptionCodes({
            ...redemptionCodes,
            orderBy: orderByEvent,
            descending: !descEvent,
        })
    }

    const handleDownload = () => {
        getRedemptionCodesByIdReport(get(inputs, 'redemptionGroupId'))
            .then(r => downloadFile(r))
            .catch(e => {
                const errorList = e.response.data.errors.map(e => e.message)
                this.setState({ error: true, errorMessage: errorList })
            })
        //}
    }

    const prevInputsState = usePrevious(inputs)

    const today = moment()
    const [startDateInputEditable, setStartDateInputEditable] = useState(
        today.isSameOrAfter(moment(get(inputs, 'startDate')), 'day') &&
        today.isBefore(moment(get(inputs, 'endDate')), 'day') &&
        today.isSameOrAfter(moment(get(prevInputsState, 'startDate')), 'day') &&
        today.isBefore(moment(get(prevInputsState, 'endDate')), 'day')
    )

    useEffect(() => {
        setStartDateInputEditable(
            today.isSameOrAfter(moment(get(inputs, 'startDate')), 'day') &&
            today.isBefore(moment(get(inputs, 'endDate')), 'day') &&
            today.isSameOrAfter(moment(get(prevInputsState, 'startDate')), 'day') &&
            today.isBefore(moment(get(prevInputsState, 'endDate')), 'day')
        )
    }, [isEditing])

    return (
        <>
            <div className="card mb-3">
                <LoadingOverlay active={loading} spinner styles={customLoadingOverlay} >
                    <PopupError
                        open={!isEmpty(apiErrors)}
                        onClose={handleErrorClose}
                        onClick={handleErrorClose}
                        errorMessage={apiErrors}
                    />

                    <div className="card-header text-center">
                        {haveDataFromLocation ? 'Edit Redemption Code' : 'Add Redemption Codes'}
                    </div>
                    <div className="card-body fs-12">
                        <Form onSubmit={handleSubmit}>
                            <RequiredFieldNotation />
                            <div className="row">
                                <div className="col-md-6">
                                    <Col className={get(inputError, 'name') ? 'has-error' : ''}>
                                        <Label htmlFor='name' className="required-hint mt-4">
                                            <strong>Name</strong>
                                        </Label>
                                        <Input
                                            id="name"
                                            type="input"
                                            name="name"
                                            maxLength="30"
                                            autoComplete="off"
                                            className="input-editable"
                                            defaultValue={get(inputs, 'name') || get(inputs, 'redemptionName')}
                                            onChange={handleInputChange}
                                            readOnly={isEditing}
                                        />
                                        {get(inputError, 'name') && (
                                            <span className="help-block">{get(inputError, 'name')}</span>
                                        )}
                                    </Col>
                                    <Col className={get(inputError, 'productId') ? 'has-error' : ''}>
                                        <Label htmlFor='productName' className="mt-4">
                                            <strong>Product</strong>
                                        </Label>
                                        {haveDataFromLocation ? (
                                            <Input
                                                id="productName"
                                                type="input"
                                                name="productName"
                                                maxLength="30"
                                                className="input-editable"
                                                defaultValue={get(inputs, 'productName')}
                                                readOnly
                                            />
                                        ) : (
                                            <div className={haveDataFromLocation ? 'select-disabled' : 'select'}>
                                                <select
                                                    id="productName"
                                                    className="input-editable form-control"
                                                    value={get(inputs, 'productId')}
                                                    title=""
                                                    name="productId"
                                                    onChange={handleInputChange}
                                                    onBlur={handleInputChange}
                                                >
                                                    {renderOptions({ optionValues: products, idKey: 'productId' })}
                                                </select>
                                            </div>
                                        )}
                                        {get(inputError, 'productId') && (
                                            <span className="help-block">{get(inputError, 'productId')}</span>
                                        )}
                                    </Col>


                                    {!isEditing && <Row>
                                        <div className="col-md-6">
                                            <Col className={get(inputError, 'startDate') ? 'has-error' : ''}>
                                                <Label htmlFor='startDate' className="required-hint mt-4"><strong>Start date</strong></Label>
                                                <CustomDatePicker
                                                    id="startDate"
                                                    readOnly={isEditing}
                                                    defValue={
                                                        get(inputs, 'startDate')
                                                            ? get(inputs, 'startDate').substring(0, 10)
                                                            : ''
                                                    }
                                                    onChange={date => handleDatePickerChange(date, 'startDate')}
                                                    label="Start Date"
                                                    overlayClass="location-datepicker-overlay"
                                                />
                                                {location.pathname === '/secure/redemptionCodesCreate' &&
                                                    <InputFeedback feedbackText={feedback.startDate} id='startDate' />
                                                }
                                                {get(inputError, 'startDate') && (
                                                    <span className="help-block-calendar">
                                                        {get(inputError, 'startDate')}
                                                    </span>
                                                )}
                                            </Col>
                                        </div>
                                        <div className="col-md-6">
                                            <Col className={get(inputError, 'endDate') ? 'has-error' : ''}>
                                                <Label htmlFor='endDate' className="required-hint mt-4"><strong>End date</strong></Label>
                                                <CustomDatePicker
                                                    id="endDate"
                                                    readOnly={isEditing}
                                                    defValue={
                                                        get(inputs, 'endDate') ? get(inputs, 'endDate').substring(0, 10) : ''
                                                    }
                                                    onChange={date => handleDatePickerChange(date, 'endDate')}
                                                    label="End Date"
                                                    overlayClass="location-datepicker-overlay"
                                                />
                                                {get(inputError, 'endDate') && (
                                                    <span className="help-block-calendar">{get(inputError, 'endDate')}</span>
                                                )}
                                            </Col>
                                        </div>
                                    </Row>}

                                    {isEditing && <div className="col-md-12">
                                        <Row>
                                            <div className={isEditing ? 'col-md-6 _startDate ' : "col-md-6 hide"}>
                                                <Label htmlFor='_startDate' className="required-hint mt-4"><strong>Start date</strong></Label>
                                                <Input
                                                    id="_startDate"
                                                    readOnly={true}
                                                    className=''
                                                    value={startDate}>
                                                </Input>

                                            </div>
                                            <div className={isEditing ? 'col-md-6 _endDate' : "col-md-6 hide"}>
                                                <Label htmlFor='_endDate' className="required-hint mt-4"><strong>End date</strong></Label>
                                                <Input
                                                    id="_endDate"
                                                    readOnly={true}
                                                    className=''
                                                    value={endDate}>
                                                </Input>
                                            </div>
                                        </Row>
                                    </div>}
                                </div>

                                <div className="col-md-6">
                                    <Col className={get(inputError, 'branchId') ? 'has-error' : ''}>
                                        <Label htmlFor='branchName' className="required-hint mt-4">
                                            <strong>Branch</strong>
                                        </Label>
                                        {haveDataFromLocation ? (
                                            <Input
                                                id="branchName"
                                                type="input"
                                                name="branchName"
                                                maxLength="30"
                                                className="input-editable"
                                                defaultValue={get(inputs, 'branchName')}
                                                readOnly
                                            />
                                        ) : (
                                            <div className={haveDataFromLocation ? 'select-disabled' : 'select'}>
                                                <select
                                                    id="branchName"
                                                    className="input-editable form-control"
                                                    value={get(inputs, 'branchId')}
                                                    title=""
                                                    name="branchId"
                                                    onChange={handleInputChange}
                                                    onBlur={handleInputChange}
                                                >
                                                    {renderOptions({ optionValues: branches, idKey: 'branchId' })}
                                                </select>
                                            </div>
                                        )}
                                        {get(inputError, 'branchId') && (
                                            <span className="help-block">{get(inputError, 'branchId')}</span>
                                        )}
                                    </Col>

                                    <Col className={get(inputError, 'qty') ? 'has-error' : ''}>
                                        <Label htmlFor='quantity' className="mt-4">
                                            <strong>Quantity</strong>
                                        </Label>
                                        <Input
                                            type="number"
                                            name="qty"
                                            id='quantity'
                                            className="input-editable"
                                            defaultValue={get(inputs, 'qty')}
                                            step="1"
                                            onChange={handleInputChange}
                                            readOnly={isEditing}
                                        />
                                        {get(inputError, 'qty') && (
                                            <span className="help-block">{get(inputError, 'qty')}</span>
                                        )}
                                    </Col>

                                    <div className="text-right float-bottom">
                                        <Button onClick={goBack} className="cancel-btn">
                                            CANCEL
                                        </Button>
                                        <Button type="submit" className="save-btn">
                                            {isEditing ? 'EDIT' : 'SAVE'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    </div>

                </LoadingOverlay>
            </div>

            {haveDataFromLocation && (
                <>
                    {!isEmpty(get(redemptionCodes, 'results') || []) && (
                        <DataTableBase
                            columns={columns}
                            isLoading={this.state.loading}
                            data={get(redemptionCodes, 'results') || []}
                            paginationTotalRows={get(redemptionCodes, 'rowCount')}
                            paginationPerPage={get(redemptionCodes, 'pageSize')}
                            paginationDefaultPage={1}
                            onChangeRowsPerPage={handlePageSizeChange}
                            onChangePage={handlePageChange}
                            onSort={handleOrderData}
                        />
                    )}

                    <div className="card-body fs-12 pt-0">
                        <div className="row">
                            <div className="col-md-6">
                                {isEditing &&
                                    <Button type="submit" onClick={handleDownload} className="save-btn mt-0 ml-0">
                                        DOWNLOAD
                                    </Button>}
                            </div>
                        </div>
                    </div>

                    <hr className="mt-1" />
                    <div>
                        <Col>
                            <Row>
                                <div className="col-md-1">
                                    <strong>ID</strong>
                                    <p className="editFooterText">{get(inputs, 'redemptionGroupId')}</p>
                                </div>
                                <div className="col-md-3">
                                    <strong>Created by</strong>
                                    <p className="editFooterText">{get(inputs, 'createdBy')}</p>
                                </div>
                                <div className="col-md-3">
                                    <strong>Created date</strong>
                                    <p className="editFooterText">{formatInTimeZoneDateTime(get(inputs, 'createdDate'), branch.branchIanaTimeZoneId)}</p>
                                </div>
                                <div className="col-md-2">
                                    <strong>Updated by</strong>
                                    <p className="editFooterText">{get(inputs, 'updatedBy')}</p>
                                </div>
                                <div className="col-md-3">
                                    <strong>Updated date</strong>
                                    <p className="editFooterText">{formatInTimeZoneDateTime(get(inputs, 'updatedDate'), branch.branchIanaTimeZoneId)}</p>
                                </div>
                            </Row>
                        </Col>
                    </div>
                </>
            )}
        </>
    )
}

export default RedemptionCodesCreate
